import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import type { Database } from '../types/database.types';
import { useCompanyData } from '../hooks/useCompanyData';
import { useMemo, useState } from 'react';

type EmployeeWithCompanyName = Database['public']['Tables']['employees']['Row'] & {
  companies: { name: string } | null;
};

type EmployeeFormData = {
  full_name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive';
  company_id?: string;
};

export default function EmployeeManagement() {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<EmployeeFormData>();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: companyId, isLoading: isLoadingCompanyId } = useQuery<string | null>({
    queryKey: ['companyId'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_current_user_company_id');
      if (error) {
        console.error("Error fetching companyId:", error);
        throw new Error(`RPC Error: ${error.message}`);
      }
      return data as string | null;
    },
    staleTime: Infinity,
  });

  const { data: currentUser, isLoading: isLoadingUser } = useQuery<User | null>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: 5 * 60 * 1000,
  });

  const isSuperAdmin = useMemo(() =>
    currentUser?.user_metadata?.role === 'super_admin',
    [currentUser]
  );

  // Haal de rol van de huidige gebruiker op
  const { data: currentEmployeeRole } = useQuery<string | null>({
    queryKey: ['currentEmployeeRole', companyId, currentUser?.id],
    queryFn: async () => {
      if (!companyId || !currentUser?.id) return null;
      const { data, error } = await supabase
        .from('employees')
        .select('role')
        .eq('company_id', companyId)
        .eq('user_id', currentUser.id)
        .maybeSingle();
      if (error) {
        console.error('Error fetching current user role:', error);
        return null;
      }
      return data?.role || null;
    },
    enabled: !!companyId && !!currentUser?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Check of de gebruiker admin rechten heeft
  const isAdmin = isSuperAdmin || currentEmployeeRole === 'admin';
  
  const { data: companyData, isLoading: isLoadingData, isError } = useCompanyData(companyId || undefined);

  const employees = useMemo(() =>
    companyData?.employees as EmployeeWithCompanyName[] | undefined,
    [companyData]
  );
  
  const companies = useMemo(() =>
    companyData?.companies,
    [companyData]
  );

  const createMutation = useMutation({
    mutationFn: async (formData: EmployeeFormData) => {
      if (!companyId) throw new Error('Geen bedrijf beschikbaar');
      
      const targetCompanyId = isSuperAdmin && formData.company_id ? formData.company_id : companyId;
      
      if (!targetCompanyId) throw new Error('Doelbedrijf kon niet worden bepaald.');

      const { data: newEmployee, error: insertError } = await supabase
        .from('employees')
        .insert({
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          status: formData.status,
          company_id: targetCompanyId,
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(`Medewerker kon niet worden toegevoegd: ${insertError.message}`);
      }

      return newEmployee;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companyData', companyId] });
      toast.success(
        `Medewerker ${data.full_name} toegevoegd! ` +
        `De medewerker kan nu een account aanmaken via de signup pagina met email: ${data.email}`
      );
      reset();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'active' | 'inactive' }) => {
      const { error } = await supabase
        .from('employees')
        .update({ status })
        .eq('id', id);
      
      if (error) throw new Error(`Status kon niet worden bijgewerkt: ${error.message}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData', companyId] });
      toast.success('Status bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      setDeletingId(id);
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', id);
      
      if (error) {
        if (error.message.includes('last admin')) {
          throw new Error('Kan laatste admin niet verwijderen. Voeg eerst een andere admin toe.');
        }
        throw new Error(`Medewerker kon niet worden verwijderd: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData', companyId] });
      toast.success('Medewerker verwijderd');
      setDeletingId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setDeletingId(null);
    },
  });

  const onSubmit = (data: EmployeeFormData) => createMutation.mutate(data);

  const isLoading = isLoadingUser || isLoadingCompanyId || isLoadingData;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Gegevens worden geladen...</p>
        </div>
      </div>
    );
  }

  if (isError || !companyId) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-600">
        Fout: Geen bedrijf gevonden of data kon niet geladen worden.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">Medewerkersbeheer</h1>
      <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-2' : ''} gap-8`}>
        {isAdmin && (
          <div>
            <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Nieuwe Medewerker</h2>
            {isSuperAdmin && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Bedrijf</label>
                <select 
                  {...register('company_id', { required: isSuperAdmin })} 
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Selecteer een bedrijf</option>
                  {companies?.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.company_id && (
                  <p className="text-red-500 text-xs mt-1">Bedrijf is verplicht.</p>
                )}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Naam</label>
              <input 
                {...register('full_name', { required: 'Naam is verplicht' })} 
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
              />
              {errors.full_name && (
                <p className="text-red-500 text-xs mt-1">{errors.full_name.message}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input 
                type="email" 
                {...register('email', { 
                  required: 'Email is verplicht',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Ongeldig email adres'
                  }
                })} 
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                De medewerker kan zich registreren op /auth met dit email adres.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Rol</label>
              <select 
                {...register('role', { required: 'Rol is verplicht' })} 
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Selecteer een rol</option>
                <option value="employee">Medewerker</option>
                <option value="admin">Admin</option>
                {isSuperAdmin && <option value="super_admin">Super Admin</option>}
              </select>
              {errors.role && (
                <p className="text-red-500 text-xs mt-1">{errors.role.message}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select 
                {...register('status')} 
                defaultValue="active" 
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="active">Actief</option>
                <option value="inactive">Inactief</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={createMutation.isPending || isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-2 px-4 rounded-md transition-colors duration-200"
            >
              {createMutation.isPending ? 'Toevoegen...' : 'Medewerker Toevoegen'}
            </button>
          </form>
        </div>
        )}

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Huidige Medewerkers</h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {employees && employees.length > 0 ? (
              employees.map(emp => (
                <div key={emp.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{emp.full_name}</p>
                      <p className="text-sm text-gray-600 truncate">{emp.email}</p>
                      <p className="text-sm text-gray-500 mt-1">Rol: {emp.role}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          emp.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {emp.status === 'active' ? 'Actief' : 'Inactief'}
                        </span>
                        {emp.user_id && (
                          <span className="text-xs px-2 py-1 rounded font-medium bg-blue-100 text-blue-800">
                            Account gekoppeld
                          </span>
                        )}
                      </div>
                      {isSuperAdmin && emp.companies?.name && (
                        <p className="text-xs text-blue-600 mt-1 font-medium">{emp.companies.name}</p>
                      )}
                    </div>

                    {isAdmin && (
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => updateStatusMutation.mutate({
                            id: emp.id,
                            status: emp.status === 'active' ? 'inactive' : 'active'
                          })}
                          className="text-xs px-3 py-1.5 rounded font-medium bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-200 transition-colors disabled:opacity-50"
                          disabled={updateStatusMutation.isPending}
                        >
                          {emp.status === 'active' ? 'Deactiveren' : 'Activeren'}
                        </button>

                        <button
                          onClick={() => {
                            if (window.confirm(
                              `Weet je zeker dat je ${emp.full_name} wilt verwijderen?\n\n` +
                              `Let op: Dit verwijdert ook alle gekoppelde time entries, kilometers en costs.`
                            )) {
                              deleteMutation.mutate(emp.id);
                            }
                          }}
                          className="text-xs px-3 py-1.5 rounded font-medium bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition-colors disabled:opacity-50"
                          disabled={deleteMutation.isPending || deletingId === emp.id}
                        >
                          {deletingId === emp.id ? 'Verwijderen...' : 'Verwijderen'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-base">Geen medewerkers gevonden.</p>
                <p className="text-sm mt-2">Voeg de eerste medewerker toe via het formulier.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}